import { queryOptions } from "@tanstack/react-query";

import {
  getRecordById,
  getRecordByIdWithMetadata,
  getRecordCollection,
  getRecordCollectionWithMetadata,
  getRecordDereferenced,
} from "~/api/generated/record-service/business-object/business-object";
import { getAllRelationships } from "~/api/generated/record-service/relationship/relationship";
import type { CollectionFacet } from "~/features/businessObjects/utils/collectionFacets";
import {
  type CollectionQueryEngine,
  fetchCollectionQuery,
  isCollectionTableMode,
} from "~/features/businessObjects/utils/collectionQuery";
import type {
  CollectionGroupTree,
  CollectionQueryTotals,
} from "~/features/businessObjects/utils/collectionQueryGroups";
import {
  fetchChildUnderParent,
  type ParentScopedChildQuery,
} from "~/features/businessObjects/utils/parentScopedChildQuery";
import {
  markSchemaWithoutRelationships,
  schemaSupportsRelationships,
} from "~/features/businessObjects/utils/schemaRelationshipsAvailability";
import { hasApiErrorStatus } from "~/lib/apiClient";
import { withRecordFetchSlot } from "~/lib/concurrency";

import { dedupeCollectionRecords, dedupeRecordsByPk } from "../utils/dedupeCollectionRecords";
import { type BusinessObjectMetadata } from "../utils/normalizeMetadata";
import { normalizeRecordRelationships } from "../utils/normalizeRecordRelationships";
import { RELATIONSHIPS_RESTRICTED } from "../utils/recordCapabilities";
import {
  extractAuditMetadata,
  parseRecordCollection,
  parseRecordDetail,
  type RecordWithMeta,
} from "./recordParsers";
import { unwrapDereferenced } from "./recordPayloads";
import { type RecordCollectionParams, recordKeys } from "./recordQueryKeys";
import type { ResourceRef } from "./recordRef";

/**
 * Generic `queryOptions` builders for the record-service.
 *
 * Every dynamic-resource entity (`Material`, `Release`, …) flows through the
 * same `/record-service/v1/{resource}/{version}/...` endpoints, so each
 * builder accepts a `ResourceRef` and produces a `queryOptions(...)` object
 * suitable for `useQuery`, `useSuspenseQuery`, `prefetchQuery`, or
 * `getQueryData` lookups.
 *
 * 404 handling: the BE returns 404 when no records exist for a registered
 * schema (or when a specific PK is missing). Treating this as a successful
 * empty result is what every call site has historically wanted; throw paths
 * are reserved for genuine network / 5xx failures, so error boundaries fire
 * only when something is actually wrong.
 *
 * The empty result for a DETAIL read is `null`, never `undefined`. TanStack
 * Query forbids a `queryFn` that resolves `undefined` — it throws "Query data
 * cannot be undefined", marks the query errored, and every consumer then
 * refetch-loops against the same missing record (a detail page for a pk that
 * does not exist logged the throw 500+ times and never painted a body). `null`
 * is a legal cached value, so the miss settles as a success. Consumer-facing
 * contracts stay on `undefined`: the hook boundary (`useBusinessObject`,
 * `useRecord`) converts `null` there.
 */

export interface PaginatedRecords {
  items: Array<{
    data: Record<string, unknown>;
    metadata: BusinessObjectMetadata;
  }>;
  /** `null` when the BE returned 404 (treated as empty) — distinguishes
   *  "we never fetched" from "fetched, BE says nothing here". */
  total: number | null;
  /**
   * Enum facet buckets from `POST /query`, `[]` on every other read path.
   *
   * Optional on the TYPE, always present on a value this module produces: the
   * optimistic primitives and cache patches build pages by spreading an existing
   * one, and a seeded test page has no facets to speak of. Read it as
   * `page.facets ?? []`.
   */
  facets?: CollectionFacet[];
  /** Engine that served the read (`meta.engine`), when reported. */
  engine?: CollectionQueryEngine;
  /** Full-corpus group tree from Phase 1 POST `/query` — omit on cache patches. */
  groups?: CollectionGroupTree;
  /** Full-corpus aggregation totals from Phase 1 POST `/query`. */
  totals?: CollectionQueryTotals;
}

export interface RecordDetail extends Record<string, unknown> {
  _metadata?: BusinessObjectMetadata;
}

/**
 * Every builder below is gated on the tenant as well as the ref, so a call site
 * cannot fire an untenanted request by forgetting `enabled: !!tenantId`.
 * `tenantId` is a parameter, not a read of the module-level `requestContext`
 * singleton — the singleton is not reactive, so reading it here would evaluate
 * once and go stale the moment the user switches tenant, exactly the bug the
 * guard exists to prevent. Passing it keeps `enabled` recomputing on every
 * render, and it is already in the query key, so the two can never disagree.
 *
 * CAVEAT for call sites: `useQuery({ ...builder(...), enabled: x })` REPLACES
 * this — object spread, not composition. Most callers here already repeat
 * `!!tenantId` in their own expression; a new one that needs an extra condition
 * must do the same (or read `.enabled` off the built options and `&&` it).
 */
const enabledWhenRefDefined = (ref: ResourceRef, tenantId?: string | null): boolean =>
  !!tenantId && !!ref.resource && !!ref.version;

/** Hidden or missing record — 404 and 403 both resolve empty so existence does not leak. */
const isRecordMissStatus = (status: number | undefined): boolean =>
  status === 404 || status === 403;

const isRecordMissError = (error: unknown): boolean =>
  hasApiErrorStatus(error, 404) || hasApiErrorStatus(error, 403);

/**
 * `GET /record-service/v1/{resource}/{version}/metadata` — list with audit
 * metadata. The default for tables and lists.
 *
 * A paged table read drops only repeats of the same pk, and reduces the
 * server `total` by that count so the footer never claims more rows than the
 * page shows. Records that share a business key under different pks stay
 * visible: collapsing them per page broke paging (see `dedupeRecordsByPk`).
 * The unpaged legacy list still collapses party resources on business keys
 * (`dedupeCollectionRecords`), where its total is the list length.
 */
export const recordCollectionOptions = (
  ref: ResourceRef,
  tenantId?: string | null,
  params?: RecordCollectionParams,
) =>
  queryOptions({
    queryKey: recordKeys.collection(ref, tenantId, params),
    enabled: enabledWhenRefDefined(ref, tenantId),
    queryFn: async ({ signal }): Promise<PaginatedRecords> => {
      if (isCollectionTableMode(params)) {
        // Deliberately outside the 404 guard below. `POST /query` answers 404
        // only when the schema lens cannot be resolved ("No schema found for
        // {resource}/{version}") — a broken read path, not an empty
        // collection, which comes back 200 with an empty list. Swallowing it
        // rendered a confident "No records to display" over a live table.
        const { records, total, facets, engine, groups, totals } = await fetchCollectionQuery({
          resource: ref.resource,
          version: ref.version,
          params,
          signal,
        });

        const deduped = dedupeRecordsByPk(records);
        const removed = records.length - deduped.length;

        return {
          items: deduped.map((record) => ({
            data: record.data,
            metadata: extractAuditMetadata(record),
          })),
          // Drop page-local dupes from the reported total so the footer
          // doesn't claim more rows than the grid shows on this page.
          total: total == null ? null : Math.max(0, total - removed),
          facets,
          engine,
          groups,
          totals,
        };
      }

      try {
        const response = await getRecordCollectionWithMetadata(
          ref.resource,
          ref.version,
          undefined,
          {
            signal,
          },
        );

        // GET does not throw (customFetch throwOnError is POST-only). A 404
        // body is `{ status, message }` — parse that and the console reports
        // an unexpected collection shape for every missing flavour table.
        if (isRecordMissStatus(response.status)) {
          return { items: [], total: null, facets: [] };
        }

        const records: RecordWithMeta[] = dedupeCollectionRecords(
          ref.resource,
          parseRecordCollection(response.data),
        );

        return {
          items: records.map((record) => ({
            data: record.data,
            metadata: extractAuditMetadata(record),
          })),
          // Legacy metadata list has no server total — length is the full set.
          total: records.length,
          facets: [],
        };
      } catch (error) {
        if (hasApiErrorStatus(error, 404)) {
          return { items: [], total: null, facets: [] };
        }

        throw error;
      }
    },
  });

/**
 * `GET /record-service/v1/{resource}/{version}` — list *without* audit
 * metadata. Cheaper than the metadata variant; use when only the BO fields
 * matter (e.g. release-id lookups).
 */
export const recordCollectionBareOptions = (ref: ResourceRef, tenantId?: string | null) =>
  queryOptions({
    queryKey: [...recordKeys.collection(ref, tenantId), "bare"] as const,
    enabled: enabledWhenRefDefined(ref, tenantId),
    queryFn: async ({ signal }): Promise<Record<string, unknown>[]> => {
      try {
        const response = await getRecordCollection(ref.resource, ref.version, undefined, {
          signal,
        });

        const records = parseRecordCollection(response.data);

        return records.map((r) => r.data);
      } catch (error) {
        if (hasApiErrorStatus(error, 404)) {
          return [];
        }

        throw error;
      }
    },
  });

/**
 * `GET /record-service/v1/{resource}/{version}/{pk}/metadata` — detail with
 * audit metadata. The audit fields are flattened under `_metadata` on the
 * returned record so they don't collide with BO field names.
 *
 * ONE empty sentinel: `null`. "Missing record" (404) and "body we could not
 * read" both resolve to it, so no consumer has to distinguish two flavours of
 * miss to answer "is there a record here?". It is `null` rather than `undefined`
 * because TanStack Query rejects an `undefined` resolution outright — see the
 * module doc above. Genuine failures still reject and land in the query's
 * `error`.
 */
export const recordDetailOptions = (ref: ResourceRef, objectId: string, tenantId?: string | null) =>
  queryOptions({
    queryKey: recordKeys.detail(ref, objectId, tenantId),
    enabled: enabledWhenRefDefined(ref, tenantId) && !!objectId,
    queryFn: async ({ signal }): Promise<RecordDetail | null> => {
      try {
        const response = await getRecordByIdWithMetadata(
          ref.resource,
          ref.version,
          objectId,
          undefined,
          { signal },
        );

        // GETs don't throw on HTTP errors (`customFetch` passes
        // `throwOnError: false`), so a missing or hidden record surfaces here
        // as 404/403 rather than a rejection. 403 is treated as a miss so the
        // UI cannot confirm the PK exists. Checked before parsing so an empty
        // body is not reported as an unrecognised shape.
        if (isRecordMissStatus(response.status)) {
          return null;
        }

        const record = parseRecordDetail(response.data);
        if (!record) {
          return null;
        }

        return {
          ...record.data,
          _metadata: extractAuditMetadata(record),
        };
      } catch (error) {
        if (isRecordMissError(error)) {
          return null;
        }

        throw error;
      }
    },
  });

/**
 * `GET …/{parentPk}/relationships/…/children/…/{childPk}`.
 * Same miss contract as {@link recordDetailOptions}: 404 and 403 are `null`.
 * The fetch does not call the global record route.
 */
export const childUnderParentDetailOptions = (
  scope: ParentScopedChildQuery,
  childPk: string,
  tenantId?: string | null,
) =>
  queryOptions({
    queryKey: [
      ...recordKeys.detail(
        { resource: scope.childResource, version: scope.childVersion },
        childPk,
        tenantId,
      ),
      "under-parent",
      scope.parentResource,
      scope.parentPk,
      scope.relationship,
    ] as const,
    queryFn: async ({ signal }): Promise<RecordDetail | null> =>
      fetchChildUnderParent(scope, childPk, signal),
  });

/**
 * `GET /record-service/v1/{resource}/{version}/{pk}` — bare detail (no audit)
 * metadata, no envelope). Use when the caller needs the raw BO fields and
 * nothing else.
 *
 * Same empty sentinel as `recordDetailOptions`: `null` for every miss.
 */
export const recordDetailBareOptions = (
  ref: ResourceRef,
  objectId: string,
  tenantId?: string | null,
) =>
  queryOptions({
    queryKey: [...recordKeys.detail(ref, objectId, tenantId), "bare"] as const,
    enabled: enabledWhenRefDefined(ref, tenantId) && !!objectId,
    queryFn: async ({ signal }): Promise<Record<string, unknown> | null> => {
      try {
        const response = await getRecordById(ref.resource, ref.version, objectId, undefined, {
          signal,
        });

        if (isRecordMissStatus(response.status)) {
          return null;
        }

        const record = parseRecordDetail(response.data);

        return record?.data ?? null;
      } catch (error) {
        if (isRecordMissError(error)) {
          return null;
        }

        throw error;
      }
    },
  });

/**
 * `GET /record-service/v1/{resource}/{version}/{pk}/relationships` — full
 * relationships map for one record.
 */
export const recordRelationshipsOptions = (
  ref: ResourceRef,
  objectId: string,
  tenantId?: string | null,
) =>
  queryOptions({
    queryKey: recordKeys.relationships(ref, objectId, tenantId),
    enabled: enabledWhenRefDefined(ref, tenantId) && !!objectId,
    queryFn: async ({ signal }) => {
      if (!schemaSupportsRelationships(ref.resource, ref.version)) {
        return {};
      }

      try {
        const response = await getAllRelationships(ref.resource, ref.version, objectId, {
          signal,
        });
        // Orval lists 200 | 404; a withheld map is 403 at runtime (GETs do not throw).
        if ((response.status as number) === 403) {
          return RELATIONSHIPS_RESTRICTED;
        }
        if (response.status === 404) {
          markSchemaWithoutRelationships(ref.resource, ref.version);
          return {};
        }

        return normalizeRecordRelationships(response.data);
      } catch (error) {
        if (hasApiErrorStatus(error, 403)) {
          return RELATIONSHIPS_RESTRICTED;
        }
        throw error;
      }
    },
  });

/**
 * `GET /record-service/v1/{resource}/{version}/{pk}/dereference?depth=N` —
 * record with recursively resolved outbound relationships.
 *
 * `depth` is part of the query key, so callers sharing this record must pass the
 * SAME depth or each pays for its own request. Table cells should prefer
 * `hydrateEdgeByIds` / `hydrateChildrenByParentEdge` over raising this depth.
 */
export const recordDereferenceOptions = (
  ref: ResourceRef,
  objectId: string,
  depth?: number,
  tenantId?: string | null,
) =>
  queryOptions({
    queryKey: recordKeys.dereference(ref, objectId, depth, tenantId),
    enabled: enabledWhenRefDefined(ref, tenantId) && !!objectId,
    queryFn: async ({ signal }) => {
      try {
        // Through the shared gate: a table of per-row `useQueries` would
        // dispatch every dereference at once. Gating here bounds every caller.
        const response = await withRecordFetchSlot(() =>
          getRecordDereferenced(
            ref.resource,
            ref.version,
            objectId,
            depth !== undefined ? { depth } : undefined,
            { signal },
          ),
        );

        // `unwrapDereferenced` answers `{}` for a body that is not an object;
        // this builder has always distinguished "no record" from "empty
        // record", so the miss resolves `null` — distinguishable from `{}`,
        // and legal as cached data where `undefined` is not — and only real
        // bodies are unwrapped.
        if (isRecordMissStatus(response.status)) {
          return null;
        }
        if (response.data == null || typeof response.data !== "object") {
          return null;
        }

        return unwrapDereferenced(response.data);
      } catch (error) {
        if (isRecordMissError(error)) {
          return null;
        }

        throw error;
      }
    },
  });
